/**
 * API der eigenen Bestellstrecke — Typen + Abruf-Helfer fuer /api/webinar-lp/funnel/….
 *
 * Die Typen SPIEGELN node-app/src/services/lpFunnel/types.ts (dort die Quelle, hier eine Kopie —
 * das Frontend importiert nicht aus node-app). Bei Aenderungen dort hier nachziehen.
 * Oberflaeche: funnel/LpFunnelPageDialog.tsx (B5) + VisualEditor.tsx (B6).
 *
 * Alle Pfade der Phase-1-Endpunkte stehen in FUNNEL_PFADE — weicht ein Builder-Router davon ab,
 * wird es hier an EINER Stelle nachgezogen.
 */
import { api, fetchData } from '../../../helpers/apiUtils';

// ---------------------------------------------------------------------------------------------
// Grundtypen
// ---------------------------------------------------------------------------------------------

export type FunnelPageType = 'order' | 'upsell' | 'thanks';
export const FUNNEL_PAGE_TYPES: FunnelPageType[] = ['order', 'upsell', 'thanks'];

export type StichtagWert = 'vor' | 'nach';
export type HerkunftWert = 'live' | 'wiederholung' | 'aufzeichnung' | 'nachfass' | 'sonst';
export const HERKUNFT_WERTE: HerkunftWert[] = [
  'live',
  'wiederholung',
  'aufzeichnung',
  'nachfass',
  'sonst',
];

export type ZeitToken =
  | 'STICHTAG_DATUM'
  | 'STICHTAG_ZEIT'
  | 'STICHTAG_RELATIV'
  | 'PREMIERE_DATUM'
  | 'PREMIERE_ZEIT'
  | 'PREMIERE_RELATIV';

export type VersionSource = 'bestand' | 'upload' | 'editor' | 'code' | 'settings' | 'restore';

export interface FunnelSettings {
  /** Berliner Wallclock 'YYYY-MM-DD HH:mm:ss'. */
  stichtagAt: string | null;
  upsellProdukt: string | null;
}
export interface FunnelSettingsInfo extends FunnelSettings {
  vorhanden: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** Teilfelder eines Patches (Schluessel wie PUT /pages); nicht gesendet = bleibt. */
export interface FunnelPatchFields {
  headline?: string;
  subheadline?: string | null;
  bullets?: string[];
  body_html?: string | null;
  emblem_image_url?: string | null;
  hero_bg_url?: string | null;
  hero_bg_scrim?: number | null;
  speaker_image_url?: string | null;
  speaker_name?: string | null;
  vimeo_video_id?: string | null;
  cta_url?: string | null;
  cta_text?: string | null;
  cta_reveal_wallclock_s?: number | null;
  cta_reveal_playback_s?: number | null;
  exit_popup_headline?: string | null;
  exit_popup_body_html?: string | null;
  plx_campaign_id?: string | null;
  price_text?: string | null;
  design_variant?: 'electric' | 'dossier' | 'show' | 'bp';
  scarcity_timer?: boolean;
  typography?: unknown;
}

export interface PatchFunnelResult {
  warnings: string[];
  versionId: number;
  contentHash: string;
  bestandVersionId: number | null;
  /** Gespeicherter (bereinigter) Stand nach dem Schreiben — Bezugsstand des HTML-Reiters. */
  stand?: { headline: string | null; body_html: string | null };
}

// ---------------------------------------------------------------------------------------------
// Versionen
// ---------------------------------------------------------------------------------------------

export type FehlendKlasse = 'ungenutzte_regel' | 'nachreichen';

export interface UploadReport {
  pageType: FunnelPageType;
  quelle: {
    /** 'kopie' = Seite eines früheren Webinars übernommen (POST …/kopieren) — Bericht ohne Dateien. */
    art: 'zip' | 'ordner' | 'html' | 'kopie';
    dateien: number;
    bytes: number;
    htmlDatei: string | null;
  };
  titel: string;
  fehlend: string[];
  /**
   * Je fehlender Datei die Einstufung des Servers (services/lpFunnel/types.ts UploadReport):
   * 'ungenutzte_regel' = nur aus CSS-Regeln referenziert, die kein Element treffen; sonst
   * 'nachreichen'. Fehlt bei aelteren Berichten → alles gilt als „nachreichen".
   */
  fehlendDetails?: Array<{ pfad: string; klasse: FehlendKlasse }>;
  umgezogen: Array<{ von: string; nach: string; bytes: number }>;
  verworfeneSkripte: number;
  /** Seit 28.10. (ältere Berichte ohne). */
  uebernommeneSkripte?: number;
  skriptBefunde?: Array<{ quelle: string; grund: string }>;
  verworfeneIframes: string[];
  pixel: string[];
  fremdHosts: string[];
  plxReste: string[];
  relativeSeitenlinks: string[];
  marker: { formular: number; countdown: number; widget: number; vimeo: number; progress: number };
  kampagne: string | null;
  bodyBytes: number;
  warnungen: string[];
}

export interface FunnelPageVersionSummary {
  id: number;
  webinarId: string;
  pageType: FunnelPageType;
  source: VersionSource;
  contentHash: string;
  restoredFrom: number | null;
  createdBy: string | null;
  createdAt: string;
  report: UploadReport | null;
}

/** Inhalt in DB-Form (bullets/typography als JSON-Text). */
export type FunnelVersionContent = Record<string, unknown> & {
  headline: string;
  body_html: string | null;
  plx_campaign_id: string | null;
  price_text: string | null;
  scarcity_timer: number;
};

export interface FunnelPageVersion extends FunnelPageVersionSummary {
  content: FunnelVersionContent;
}

export interface RestoreVersionResponse extends PatchFunnelResult {
  restoredFrom: number;
  hashGleich: boolean;
}

// ---------------------------------------------------------------------------------------------
// Upload (B1)
// ---------------------------------------------------------------------------------------------

export interface UploadDryRunResponse {
  uploadToken: string;
  report: UploadReport;
}
export interface UploadApplyResponse extends PatchFunnelResult {
  report: UploadReport;
}

// ---------------------------------------------------------------------------------------------
// Pruefung (B3)
// ---------------------------------------------------------------------------------------------

export interface RegelBefund {
  regel: string;
  schwere: 'fehler' | 'warnung' | 'info';
  seite: FunnelPageType | 'strecke';
  text: string;
  stelle?: string;
  /** Dashboard-Pfad zum Beheben (z. B. '/plx-orders/config'). */
  link?: string;
}

/** Spiegel von node-app services/lpFunnel/types.ts KampagnenPruefung / KampagnenInfoResponse. */
export type KampagnenPruefung =
  | {
      status: 'ok';
      kampagneId: string;
      wkz: string;
      produktTitel: string | null;
      preis: string | null;
      config: { plxProduct: string } | null;
    }
  | { status: 'nicht_gefunden'; kampagneId: string }
  | { status: 'fehler'; kampagneId: string; grund: string };
export interface KampagnenInfo {
  kampagne: KampagnenPruefung;
  erwartetesProdukt: { code: string; name: string | null } | null;
  befunde: RegelBefund[];
}
export interface KiBefund {
  seite: FunnelPageType;
  kategorie: 'widerspruch' | 'sprache' | 'fremd' | 'skript';
  schwere: 'rot' | 'gelb';
  zitat: string;
  begruendung: string;
  vorschlag: string;
}
export interface FunnelCheckResult {
  checkId: number;
  webinarId: string;
  scopeHash: string;
  checkerVersion: string;
  status: 'running' | 'done' | 'error';
  verdict: 'ok' | 'gelb' | 'rot' | null;
  regeln: RegelBefund[];
  ki: KiBefund[] | null;
  kiAktiv: boolean;
  model: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  createdBy: string | null;
  createdAt: string;
  finishedAt: string | null;
}
export type AckReason = 'ki_findings' | 'ki_unavailable';
export interface AckResponse {
  ackId: number;
  scopeHash: string;
}
/** Quittung („Trotzdem publizieren") eines Checks — lp_funnel_publish_acks. */
export interface FunnelQuittung {
  id: number;
  checkId: number;
  scopeHash: string;
  reason: AckReason;
  ackedBy: string;
  ackedAt: string;
}
/**
 * GET /:webinarId/check (= /check/latest, routes/lpFunnel/check.ts): `scopeHash` ist der AKTUELLE
 * Strecken-Stand, `check`/`aktuell` der letzte Check genau dieses Stands, `letzter` der letzte ueberhaupt.
 */
export interface FunnelCheckLatestResponse {
  check: FunnelCheckResult | null;
  scopeHash: string;
  kiAktiv: boolean;
  regeln: RegelBefund[];
  aktuell: FunnelCheckResult | null;
  letzter: FunnelCheckResult | null;
  quittungen: FunnelQuittung[];
  /** Produkt-Unterlagen der KI-Referenz (ohne Text); null = keine, fehlt = Server ohne Produkt-Medien. */
  produktReferenz?: ProduktReferenzZeile[] | null;
}
/** snapshot.ts produktReferenzUebersicht — was die KI-Pruefung als Produkt-Referenz sieht. */
export interface ProduktReferenzZeile {
  slot: 'ausgabe' | 'quickstart';
  versionId: number;
  zeichen: number;
  gekuerzt: boolean;
  seiten: number | null;
  seitenGelesen: number | null;
  dateiName: string | null;
}
/** Fehlerkoerper von POST /publish (StreckeFehler): 422 regel_fehler, 409 ki_rot | pruefung_fehlt | hash_geaendert. */
export type StreckeFehlerCode = 'regel_fehler' | 'ki_rot' | 'pruefung_fehlt' | 'hash_geaendert';
export interface StreckeFehlerDetails {
  code?: StreckeFehlerCode | string;
  regeln?: RegelBefund[];
  ki?: KiBefund[];
}
export interface StreckePublishResponse {
  scopeHash: string;
  pages: Array<{ pageType: FunnelPageType; url: string; buildHash: string }>;
  warnings: string[];
}

// ---------------------------------------------------------------------------------------------
// Umschaltung (B4)
// ---------------------------------------------------------------------------------------------

export type SwitchZustand = Record<string, string | number | null>;
export interface SwitchSchritt {
  name: string;
  status: 'offen' | 'ok' | 'fehler' | 'uebersprungen';
  detail?: string;
  at?: string;
}
export interface SwitchBericht {
  offerMode: string | null;
  lpmAbhaengigkeiten: string[];
  hinweise: string[];
}
export interface OrderSwitchJournal {
  id: number;
  webinarId: string;
  direction: 'live' | 'zurueck';
  status: 'db_done' | 'done' | 'failed';
  vorzustand: SwitchZustand;
  nachzustand: SwitchZustand;
  schritte: SwitchSchritt[];
  bericht: SwitchBericht | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface SwitchVorbedingung {
  name: string;
  ok: boolean;
  text: string;
}
/** Was „Live schalten" bzw. „Zurückschalten" schreiben wuerde (dryRun / Ablehnung), orderSwitch.Plan. */
export interface SwitchPlan {
  vorzustand: SwitchZustand;
  nachzustand: SwitchZustand;
  konflikte?: SwitchKonflikt[];
  behalten?: string[];
}
/** POST /switch: dryRun → { vorbedingungen, journal: null, plan }; Ablehnung 409 → details = dieselbe Form. */
export interface SwitchResponse {
  vorbedingungen: SwitchVorbedingung[];
  journal: OrderSwitchJournal | null;
  plan?: SwitchPlan | null;
}
export interface SwitchKonflikt {
  spalte: string;
  nachUmschalten: string | number | null;
  aktuell: string | number | null;
}
/** POST /switch-back: 409 (ungesehene Konflikte) → details = dieselbe Form mit `konflikte`. */
export interface SwitchBackResponse {
  konflikte: SwitchKonflikt[];
  journal: OrderSwitchJournal | null;
  plan?: SwitchPlan | null;
}

// ---------------------------------------------------------------------------------------------
// Seiten-Editor (B6)
// ---------------------------------------------------------------------------------------------

export interface EditModeLocked {
  k: number;
  label: string;
}
export interface EditModeRenderResponse {
  sessionKey: string;
  ausgangsHash: string;
  html: string;
  css: string;
  locked: EditModeLocked[];
}

// ---------------------------------------------------------------------------------------------
// Abruf-Helfer (Antwortform { success, data?, error? } wie fetchData)
// ---------------------------------------------------------------------------------------------

const BASE = '/api/webinar-lp/funnel';
const seite = (webinarId: string, pageType: FunnelPageType) =>
  `${BASE}/${encodeURIComponent(webinarId)}/${pageType}`;
const json = (method: string, body?: unknown) => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/** 409 des HTML-Reiters: der Code verweist auf Dateien, die nicht bei uns liegen (rohes LPM-HTML). */
export interface AssetsFehlenDetails {
  code: 'assets_fehlen';
  anzahl: number;
  verweise: string[];
}

/**
 * `bestaetigt`: nach der Rueckfrage „Trotzdem speichern" — der Server laesst Code mit fehlenden Dateien
 * dann durch (Entwurf ist nicht oeffentlich; Veroeffentlichen sperrt die Regel asset_unerreichbar).
 */
export function patchFunnelContent(
  webinarId: string,
  pageType: FunnelPageType,
  felder: FunnelPatchFields,
  quelle: 'code' | 'editor' = 'code',
  bestaetigt = false,
): Promise<{
  success: boolean;
  data?: PatchFunnelResult;
  error?: string;
  details?: AssetsFehlenDetails | { code?: string };
}> {
  return fetchData<PatchFunnelResult>(
    `${seite(webinarId, pageType)}/content`,
    json('PATCH', bestaetigt ? { felder, quelle, bestaetigt: true } : { felder, quelle }),
  );
}

export function listFunnelVersions(webinarId: string, pageType: FunnelPageType) {
  return fetchData<FunnelPageVersionSummary[]>(`${seite(webinarId, pageType)}/versions`);
}

export function getFunnelVersion(webinarId: string, pageType: FunnelPageType, versionId: number) {
  return fetchData<FunnelPageVersion>(`${seite(webinarId, pageType)}/versions/${versionId}`);
}

export function restoreFunnelVersion(
  webinarId: string,
  pageType: FunnelPageType,
  versionId: number,
) {
  return fetchData<RestoreVersionResponse>(
    `${seite(webinarId, pageType)}/versions/${versionId}/restore`,
    json('POST'),
  );
}

export function getFunnelSettings(webinarId: string) {
  return fetchData<FunnelSettingsInfo>(`${BASE}/${encodeURIComponent(webinarId)}/settings`);
}

/** WKZ/Produkt/Preis einer PL-X-Kampagne + Befunde (dieselben Regeln wie die Pruefung); nur lesend. */
export function getKampagnenInfo(webinarId: string, kampagneId: string) {
  return fetchData<KampagnenInfo>(
    `${BASE}/${encodeURIComponent(webinarId)}/kampagne?id=${encodeURIComponent(kampagneId)}`,
  );
}

export function putFunnelSettings(webinarId: string, settings: FunnelSettings) {
  return fetchData<FunnelSettingsInfo>(
    `${BASE}/${encodeURIComponent(webinarId)}/settings`,
    json('PUT', settings),
  );
}

// Phase-1-Endpunkte (Builder B1/B3/B4/B6) — Pfade laut routes/lpFunnel/*.ts; Upload ist multipart
// und laeuft deshalb NICHT ueber fetchData (B5 baut ihn mit FormData).

export function startFunnelCheck(webinarId: string) {
  return fetchData<{ check: FunnelCheckResult; produktReferenz?: ProduktReferenzZeile[] | null }>(
    `${BASE}/${encodeURIComponent(webinarId)}/check`,
    json('POST'),
  );
}

export function getFunnelCheck(webinarId: string) {
  return fetchData<FunnelCheckLatestResponse>(`${BASE}/${encodeURIComponent(webinarId)}/check`);
}

export function ackFunnelCheck(webinarId: string, checkId: number, reason: AckReason) {
  return fetchData<AckResponse>(
    `${BASE}/${encodeURIComponent(webinarId)}/ack`,
    json('POST', { checkId, reason }),
  );
}

/** Fehlerantwort: fetchData reicht den Koerper durch, `details` traegt Code + Befunde. */
export function publishFunnelStrecke(
  webinarId: string,
  scopeHash: string,
): Promise<{
  success: boolean;
  data?: StreckePublishResponse;
  error?: string;
  details?: StreckeFehlerDetails;
}> {
  return fetchData<StreckePublishResponse>(
    `${BASE}/${encodeURIComponent(webinarId)}/publish`,
    json('POST', { scopeHash }),
  );
}

export function switchFunnelOrderUrl(
  webinarId: string,
  opts: { dryRun?: boolean; fortsetzenId?: number } = {},
) {
  return fetchData<SwitchResponse>(
    `${BASE}/${encodeURIComponent(webinarId)}/switch`,
    json('POST', opts),
  );
}

export function switchFunnelBack(
  webinarId: string,
  opts: { dryRun?: boolean; bestaetigteKonflikte?: string[] } = {},
) {
  return fetchData<SwitchBackResponse>(
    `${BASE}/${encodeURIComponent(webinarId)}/switch-back`,
    json('POST', opts),
  );
}

// ---------------------------------------------------------------------------------------------
// Ergaenzungen B5 (Dialog): Journal, Upload (multipart), bestehende Seiten-Endpunkte
// ---------------------------------------------------------------------------------------------

/** GET /:webinarId/switches — Journal (lp_order_switches) des Webinars, neueste zuerst (B4). */
export function getFunnelSwitchJournal(webinarId: string) {
  return fetchData<OrderSwitchJournal[]>(`${BASE}/${encodeURIComponent(webinarId)}/switches`);
}

/** Eine Datei der Quelle samt relativem Pfad (Ordner/Drag-&-Drop) bzw. Dateiname (lose Dateien, ZIP). */
export interface UploadItem {
  file: File;
  path: string;
}

/** Multipart-Feldnamen des Uploads: je Datei ein `files`-Teil und in GLEICHER Reihenfolge ein `paths`-Teil. */
export const UPLOAD_FELD_DATEI = 'files';
export const UPLOAD_FELD_PFAD = 'paths';

type ApiResult<T> = { success: boolean; data?: T; error?: string } & Record<string, unknown>;

/**
 * POST /:webinarId/:pageType/upload?dryRun=1 (B1) — Trockenlauf: der Server transformiert, schreibt
 * NICHTS und haelt das Ergebnis unter `uploadToken` fuer „Uebernehmen". Fehlerkoerper werden wie bei
 * fetchData durchgereicht (nie verschluckt); eine HTML-Fehlerseite (nginx 413) wird nicht gespreadet.
 */
export async function uploadFunnelDryRun(
  webinarId: string,
  pageType: FunnelPageType,
  items: UploadItem[],
): Promise<ApiResult<UploadDryRunResponse>> {
  const form = new FormData();
  for (const it of items) {
    form.append(UPLOAD_FELD_DATEI, it.file, it.file.name);
    form.append(UPLOAD_FELD_PFAD, it.path);
  }
  try {
    const res = await api.post(`${seite(webinarId, pageType)}/upload?dryRun=1`, form);
    return res.data;
  } catch (error: any) {
    const status: number | undefined = error?.response?.status;
    const payload = error?.response?.data;
    const extra: Record<string, unknown> = payload && typeof payload === 'object' ? payload : {};
    const fallback =
      status === 413
        ? 'Upload zu groß für den Server (413) — Dateien verkleinern oder weglassen'
        : error?.message || 'Upload fehlgeschlagen';
    const serverError = typeof extra.error === 'string' ? extra.error : null;
    return { ...extra, success: false, error: serverError || fallback };
  }
}

// ---------------------------------------------------------------------------------------------
// Seite aus früherem Webinar übernehmen + Quellarchiv (Mirko 23.10., services/lpFunnel/kopie.ts, zipExport.ts)
// ---------------------------------------------------------------------------------------------

/** Seitentypen, die sich übernehmen lassen (Bestellseite ist webinar-gebunden). */
export const KOPIERBARE_SEITEN: readonly FunnelPageType[] = ['upsell', 'thanks'];

/** Eine Auswahl: identische Live-Seiten (Headline UND Inhalt) zusammengefasst, jüngstes Webinar zuerst. */
export interface KopierQuelle {
  webinarIds: string[];
  webinare: Array<{ webinar_id: string; titel: string; start: string | null }>;
  headline: string;
  bytes: number;
  hash: string;
}

export function ladeKopierQuellen(pageType: FunnelPageType, zielWebinarId: string) {
  return fetchData<KopierQuelle[]>(
    `${BASE}/kopie-quellen/${pageType}?ziel=${encodeURIComponent(zielWebinarId)}`,
  );
}

/** Übernimmt den LIVE-Stand der Quelle als Entwurf (wie ein Upload: neue Version, alter Stand gesichert). */
export function kopiereFunnelSeite(webinarId: string, pageType: FunnelPageType, quelleWebinarId: string) {
  return fetchData<UploadApplyResponse>(
    `${seite(webinarId, pageType)}/kopieren`,
    json('POST', { quelle: quelleWebinarId }),
  );
}

/**
 * GET …/export.zip — Quellarchiv des gespeicherten Stands. Läuft über axios (Cookie-Auth) als Blob; ein
 * Fehlerkörper kommt dann ebenfalls als Blob und wird hier zu Text gelesen.
 */
export async function ladeQuellarchiv(
  webinarId: string,
  pageType: FunnelPageType,
): Promise<{ success: true; blob: Blob; dateiname: string } | { success: false; error: string }> {
  try {
    const res = await api.get(`${seite(webinarId, pageType)}/export.zip`, { responseType: 'blob' });
    return { success: true, blob: res.data as Blob, dateiname: `${webinarId}-${pageType}.zip` };
  } catch (error: any) {
    const data = error?.response?.data;
    let meldung = error?.message || 'Download fehlgeschlagen';
    if (data instanceof Blob) {
      try {
        const body = JSON.parse(await data.text());
        if (typeof body?.error === 'string') meldung = body.error;
      } catch {
        /* kein JSON — Standardmeldung */
      }
    }
    return { success: false, error: meldung };
  }
}

/** POST /:webinarId/:pageType/upload/apply (B1) — schreibt Entwurf + Version aus dem Trockenlauf. */
export function applyFunnelUpload(
  webinarId: string,
  pageType: FunnelPageType,
  uploadToken: string,
) {
  return fetchData<UploadApplyResponse>(
    `${seite(webinarId, pageType)}/upload/apply`,
    json('POST', { uploadToken }),
  );
}

/** Zeile lp_webinar_pages, soweit der Dialog sie braucht (GET /api/webinar-lp/pages/:id/:type). */
export interface FunnelPageRow {
  status: 'draft' | 'published';
  headline: string | null;
  body_html: string | null;
  plx_campaign_id: string | null;
  price_text: string | null;
  scarcity_timer: number | null;
  published_at: string | null;
  updated_at: string | null;
  updated_by?: string | null;
}

export const FUNNEL_SLUGS: Record<FunnelPageType, string> = {
  order: 'bestellen',
  upsell: 'angebot',
  thanks: 'danke',
};

export const FUNNEL_LABELS: Record<FunnelPageType, string> = {
  order: 'Bestellseite',
  upsell: 'Upsell',
  thanks: 'Danke',
};

export const funnelLiveUrl = (webinarId: string, pageType: FunnelPageType) =>
  `https://lp.finanztrends.de/w/${webinarId}/${FUNNEL_SLUGS[pageType]}/`;

/** Gespeicherter Stand als echte Seite (Cookie-Auth, withPreviewGuard serverseitig). */
export const funnelPreviewUrl = (webinarId: string, pageType: FunnelPageType) =>
  `/api/webinar-lp/pages/${encodeURIComponent(webinarId)}/${pageType}/preview.html`;

export function getFunnelPage(webinarId: string, pageType: FunnelPageType) {
  return fetchData<{ page: FunnelPageRow | null }>(
    `/api/webinar-lp/pages/${encodeURIComponent(webinarId)}/${pageType}`,
  );
}

/** Warum es keinen übernehmbaren Live-Preis gibt (Backend `LivePreisGrund`), null = Preis ist live. */
export type LivePreisGrund =
  | 'keine_seite'
  | 'nicht_publiziert'
  | 'ohne_fingerabdruck'
  | 'unveroeffentlicht'
  | 'kein_preis'
  | 'platzhalter';

/** GET /api/webinar-lp/funnel/:id/live-preis — Preiszeile der Bestellseite, wie Leser sie live sehen. */
export function getLivePreis(webinarId: string) {
  return fetchData<{ preis: string | null; grund: LivePreisGrund | null }>(
    `/api/webinar-lp/funnel/${encodeURIComponent(webinarId)}/live-preis`,
  );
}

/** Getippte Bestaetigung fuer das Depublizieren von Upsell/Danke bei publizierter Bestellseite (Spec § 6). */
export const PLX_DEPUBLIZIER_BESTAETIGUNG = 'PL-X-Prozess zeigt nicht mehr hierher';

/** POST /api/webinar-lp/pages/:id/:type/unpublish — Body `{ bestaetigung }` nur fuer Upsell/Danke. */
export function unpublishFunnelPage(
  webinarId: string,
  pageType: FunnelPageType,
  bestaetigung?: string,
) {
  return fetchData<{ warnings?: string[]; files_removed?: string[] }>(
    `/api/webinar-lp/pages/${encodeURIComponent(webinarId)}/${pageType}/unpublish`,
    json('POST', bestaetigung ? { bestaetigung } : {}),
  );
}
